import React, { useRef } from 'react';
import DialogShell from './DialogShell';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: React.ReactNode;
  error?: React.ReactNode;
  items?: string[];
  maxItemsShown?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  // Hide the secondary/cancel button for single-action info dialogs (e.g. a
  // "Got it" acknowledgement) where a second dismiss control is redundant.
  hideCancel?: boolean;
  variant?: 'destructive' | 'default';
  submitting?: boolean;
}

/**
 * Centered confirmation dialog. Cancel is the default focus (Enter doesn't
 * accidentally fire the destructive action — the user must Tab to Confirm).
 * Backdrop click and Escape both cancel. If an `items` array is passed, the
 * first `maxItemsShown` are listed; the rest collapse into "…and N more" so
 * the modal doesn't grow unbounded on large selections.
 */
export default function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  error,
  items,
  maxItemsShown = 5,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  hideCancel = false,
  variant = 'default',
  submitting = false,
}: Props) {
  const titleId = React.useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  if (!open) return null;

  const shown = items?.slice(0, maxItemsShown) ?? [];
  const remaining = items ? items.length - shown.length : 0;

  const confirmClass =
    variant === 'destructive'
      ? 'bg-danger text-white hover:opacity-90'
      : 'bg-[color:var(--primary)] text-on-primary hover:bg-[color:var(--primary-hover)]';

  return (
    <DialogShell
      open={open}
      onClose={onCancel}
      labelledBy={titleId}
      backdropPolicy={submitting ? 'static' : 'dismiss'}
      initialFocusRef={hideCancel ? confirmRef : cancelRef}
      overlayClassName="flex items-center justify-center p-4"
      panelClassName="w-full max-w-md border border-border-default overflow-hidden"
      header={(
        <h2
          id={titleId}
          className="text-base font-semibold text-fg leading-snug"
        >
          {title}
        </h2>
      )}
      headerClassName="px-5 pt-5"
      bodyClassName="px-5 pt-1.5 pb-4"
      footer={(
        <>
          {!hideCancel && (
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="min-h-8 px-3 py-1.5 text-[13px] font-medium rounded-md text-fg-secondary hover:bg-surface focus:outline-none focus-visible:shadow-bark-focus transition-colors disabled:opacity-60"
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={() => {
              if (!submitting) void onConfirm();
            }}
            disabled={submitting}
            className={`min-h-8 px-3 py-1.5 text-[13px] font-medium rounded-md focus:outline-none focus-visible:shadow-bark-focus transition-colors disabled:opacity-60 ${confirmClass}`}
          >
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </>
      )}
      footerClassName="px-5 py-3 bg-bg-warm border-t border-divider flex items-center justify-end gap-2"
    >
      {description && (
        <p className="text-[13px] text-fg-secondary leading-relaxed">
          {description}
        </p>
      )}
      {error && (
        <div role="alert" className="mt-3 text-[length:var(--text-sm)] text-danger leading-relaxed">
          {error}
        </div>
      )}
      {shown.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1">
          {/* Index-keyed because the same name could appear twice in the
              list (two projects can share a name); using the name as a
              React key would collide and misrender. */}
          {shown.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-[13px] text-fg leading-snug"
            >
              <span className="text-fg-tertiary mt-1 leading-none">•</span>
              <span className="truncate">{item}</span>
            </li>
          ))}
          {remaining > 0 && (
            <li className="text-[12px] text-fg-tertiary italic pl-3 mt-0.5">
              …and {remaining} more
            </li>
          )}
        </ul>
      )}
    </DialogShell>
  );
}
